import { useEffect, useState } from 'react'
import type { View } from '../App'

/**
 * Tiny dependency-free router. The URL is the single source of truth for where the user is:
 *   /                                  → home (all bids)
 *   /bid/:bidId                        → a bid's overview ("bid home")
 *   /bid/:bidId/knowledge              → that bid's knowledge view
 *   /bid/:bidId/settings               → that bid's settings page (members, usage, branding)
 *   /bid/:bidId/templates/:templateId  → a bid Office template in the template view (AB#454);
 *                                        `?layout=<layout id>` selects a layout, `?from=<room>&slide=`
 *                                        the deck (and slide) "← Back" returns to (lib/template-view.ts)
 *   /bid/:bidId/:deliverable/:part     → a part open in the co-editor (room = bid/deliverable/part)
 *   /settings                          → the GLOBAL account settings page (Appearance, AI agent)
 *   /settings/:section                 → the same page, opened straight at one of its sections
 *   /corporate-templates               → the corporate PowerPoint template catalogue (tenant admins)
 *   /version-retention                 → the tenant's version-retention defaults (tenant admins)
 *   /feedback-triage                   → the userhub feedback triage page (userhub admins);
 *                                        `?ticket=<id>` opens one ticket (read by the page)
 *   /tour/:n                           → a guided tour's link: the home page, with the userhub
 *                                        catalog open on tour #n (tours/deepLink.ts). `?tour=<n>`
 *                                        on any path does the same over that path's view.
 *
 * In dev the Vite server falls back to index.html for unknown paths, so deep links and
 * refresh work. (Prod history-fallback is deferred with the rest of the prod hosting.)
 */
export interface Route {
  view: View
  bid: string | null
  /** The full room key (bid/deliverable/part) when a part is open, else null. */
  room: string | null
  /** Which section to open on the global account-settings page (view === 'account-settings').
   *  Undefined (the plain `/settings` path, or any other view) falls back to the page's own
   *  default — see AccountSettingsPage's initial-state fallback. */
  section?: SettingsSection
  /** `/tour/:n` (view 'home'): the tour number its catalog opens on; 0 when `n` is not a number. */
  tour?: number
  /**
   * `/bid/:bid/templates/:id` (view 'template', AB#454): the bid Office template open in the template
   * view. The same path is also a part's shape (an artefact whose id is `templates`): App opens that
   * part instead when the bid's outline holds it — {@link TEMPLATES_SEGMENT}.
   */
  template?: string
}

/** The path segment of the template view (`/bid/:bid/templates/:id`). */
export const TEMPLATES_SEGMENT = 'templates'

/**
 * The deep-linkable sections of the account-settings page. Must stay in step with
 * AccountSettingsPage's own `Section` type — this was typed `'appearance' | 'ai-agent'` while the
 * page already had a third, so `/settings/diagnostics` silently opened the default section.
 *
 * A section being listed here only makes it ADDRESSABLE. Whether it renders at all is the page's
 * decision (diagnostics and kb-sources are both conditional), so a deep link to one the caller may
 * not see lands on the page without it — which is what a non-admin should see anyway.
 */
export const SETTINGS_SECTIONS = [
  'appearance',
  'ai-agent',
  'diagnostics',
  'kb-sources',
  'user-activity',
] as const
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]

function asSettingsSection(seg: string | undefined): SettingsSection | undefined {
  return SETTINGS_SECTIONS.find((s) => s === seg)
}

export function parseRoute(pathname: string): Route {
  const segs = pathname
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean)
    .map(decodeURIComponent)

  if (segs[0] === 'releases') return { view: 'releases', bid: null, room: null }
  // The GLOBAL account settings page (Appearance / AI agent) — distinct from settingsPath(bid)
  // below, which is the per-bid settings page (members/usage/branding). Guarded to at most two
  // segments so it cannot shadow `/bid/...` (segs[0] === 'bid' there, never 'settings'). The
  // optional second segment picks which section opens (see the *SettingsPath exports below) —
  // anything not in SETTINGS_SECTIONS (including absent) falls back to the page's default section.
  if (segs[0] === 'settings' && segs.length <= 2) {
    const section = asSettingsSection(segs[1])
    return { view: 'account-settings', bid: null, room: null, ...(section ? { section } : {}) }
  }
  if (segs[0] === 'corporate-templates' && segs.length === 1) return { view: 'corporate-templates', bid: null, room: null }
  if (segs[0] === 'version-retention' && segs.length === 1) return { view: 'version-retention', bid: null, room: null }
  if (segs[0] === 'feedback-triage' && segs.length === 1) return { view: 'feedback-triage', bid: null, room: null }
  // A tour link shows the home page behind the catalog. No tour is numbered 0, so a malformed number
  // still opens the catalog — on its "not available" message.
  if (segs[0] === 'tour' && segs.length === 2) {
    return { view: 'home', bid: null, room: null, tour: TOUR_NUMBER.test(segs[1]!) ? Number(segs[1]) : 0 }
  }
  if (segs[0] !== 'bid' || !segs[1]) return { view: 'home', bid: null, room: null }

  const bid = segs[1]
  const rest = segs.slice(2)
  if (rest[0] === 'knowledge' && rest.length === 1) return { view: 'knowledge', bid, room: null }
  if (rest[0] === 'dashboard' && rest.length === 1) return { view: 'dashboard', bid, room: null }
  if (rest[0] === 'settings' && rest.length === 1) return { view: 'settings', bid, room: null }
  if (rest[0] === TEMPLATES_SEGMENT && rest.length === 2) return { view: 'template', bid, room: null, template: rest[1] }
  if (rest.length >= 2) return { view: 'editor', bid, room: [bid, rest[0], rest[1]].join('/') }
  return { view: 'editor', bid, room: null } // bid overview (no part open)
}

const enc = (s: string): string => encodeURIComponent(s)

/** A public tour number in a link: `/tour/101`, `?tour=101`. */
export const TOUR_NUMBER = /^\d{1,6}$/

export const homePath = '/'
export const releasesPath = '/releases'
/** The global (non-bid) account settings page — Appearance + AI agent detection/install. Not to
 *  be confused with settingsPath(bid) below, the per-bid settings page (members/usage/branding). */
export const accountSettingsPath = '/settings'
/** Same page, deep-linked straight to the "AI agent" section — used by the chat panel's
 *  no-agent-detected empty state so its "Go to settings" button lands somewhere useful. */
export const aiAgentSettingsPath = '/settings/ai-agent'
/** Same page, deep-linked to the "Appearance" section (colour scheme + text size) — the account
 *  menu's "Appearance settings" entry. */
export const appearanceSettingsPath = '/settings/appearance'
/** Same page, deep-linked to the corporate KB source catalogue. Tenant-admins only — the section
 *  simply is not rendered for anyone else, and the backend refuses its endpoints regardless. */
export const kbSourcesSettingsPath = '/settings/kb-sources'
/** The corporate PowerPoint template catalogue (AB#222). The page itself says "not allowed" to
 *  anyone but a tenant admin, and the backend refuses its writes regardless. */
export const corporateTemplatesPath = '/corporate-templates'
/** The tenant's version-retention defaults (AB#245): ages, default counts, day window. Tenant
 *  admins only — the page says "not allowed" to anyone else, and the backend refuses regardless. */
export const versionRetentionPath = '/version-retention'
/** The userhub feedback triage page (AB#374). Offered in the account menu to userhub's own admin
 *  allow-list only; the widget and the userhub API check again. `?ticket=<id>` opens one ticket. */
export const feedbackTriagePath = '/feedback-triage'
export const bidPath = (bid: string): string => `/bid/${enc(bid)}`
export const knowledgePath = (bid: string): string => `/bid/${enc(bid)}/knowledge`
export const dashboardPath = (bid: string): string => `/bid/${enc(bid)}/dashboard`
export const settingsPath = (bid: string): string => `/bid/${enc(bid)}/settings`
/**
 * A bid Office template in the template view (AB#454): `layout` = the layout selected (a layout id,
 * `slideLayout7`); `from` + `slide` = the deck and slide the member came from, where "← Back"
 * returns; `element` (AB#457) = a shape of the layout to select, `<part>:<p:cNvPr id>` (the deck's
 * double-click on what belongs to the layout). Only the path is shared or handed to Desktop (which
 * drops the query).
 */
export function templateViewPath(bid: string, templateId: string, opts: { layout?: string; from?: string; slide?: string; element?: string } = {}): string {
  const qs = new URLSearchParams()
  if (opts.layout) qs.set('layout', opts.layout)
  if (opts.from) qs.set('from', opts.from)
  if (opts.slide) qs.set('slide', opts.slide)
  if (opts.element) qs.set('element', opts.element)
  const q = qs.toString()
  return `/bid/${enc(bid)}/${TEMPLATES_SEGMENT}/${enc(templateId)}${q ? `?${q}` : ''}`
}

/** What a template view's query says (`?layout=&from=&slide=&element=`); absent = none. */
export function templateViewQuery(search: string): { layout?: string; from?: string; slide?: string; element?: string } {
  const qs = new URLSearchParams(search)
  const out: { layout?: string; from?: string; slide?: string; element?: string } = {}
  for (const key of ['layout', 'from', 'slide', 'element'] as const) {
    const v = qs.get(key)
    if (v) out[key] = v
  }
  return out
}

/** A guided tour's link: opens the userhub catalog on tour #n, never starts it. */
export const tourPath = (n: number): string => `/tour/${n}`
/** room = "bid/deliverable/part" → "/bid/<bid>/<deliverable>/<part>". */
export const roomPath = (room: string): string => '/bid/' + room.split('/').map(enc).join('/')

/**
 * An absolute link to a part that a colleague can open. On the PUBLIC web origin
 * (`window.__NIOBE_CONFIG__.webUrl`) when configured — the desktop serves this SPA from
 * localhost:4100, useless to anyone else — else on the origin this client runs on (dev, and the
 * web app already on its own origin).
 */
export function partShareUrl(room: string): string {
  return shareOrigin() + roomPath(room)
}

/** The shareable link to tour #n (a row's "Copy link"), on the same public origin as partShareUrl. */
export function tourShareUrl(n: number): string {
  return shareOrigin() + tourPath(n)
}

function shareOrigin(): string {
  return (window.__NIOBE_CONFIG__?.webUrl || window.location.origin).replace(/\/+$/, '')
}

/**
 * The `niobe://` link that opens `path` in Niobe Desktop: its host + path IS the SPA pathname
 * (`/bid/a/b/c` → `niobe://bid/a/b/c`). Only the hand-off a browser offers — links that are SHARED
 * stay https (partShareUrl above), because they must work for everyone, app or no app. The desktop
 * shell validates whatever it receives against the route shapes parseRoute accepts
 * (client/desktop/niobe-deep-link.js).
 */
export function desktopDeepLink(path: string): string {
  return 'niobe://' + path.replace(/^\/+/, '')
}

/** Push a new path and notify listeners (pushState alone does not fire popstate). */
export function navigate(path: string): void {
  if (path === window.location.pathname) return
  window.history.pushState(null, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

/** Replace the current URL (no new history entry) and notify listeners, like navigate(). */
export function replaceUrl(url: string): void {
  window.history.replaceState(window.history.state, '', url)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

/** Subscribe to the current pathname (updates on navigate() and browser back/forward). */
export function usePath(): string {
  const [path, setPath] = useState(() => window.location.pathname)
  useEffect(() => {
    const onPop = (): void => setPath(window.location.pathname)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  return path
}
